import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import DefaultData from '../../CompanyData/DefaultData';

const initialFieldValues = {
    packageEnquiryId: "00000000-0000-0000-0000-000000000000",
    packageId: "00000000-0000-0000-0000-000000000000",
    companyId: "00000000-0000-0000-0000-000000000000",
    name: '',
    email: '',
    phoneNumber: '',
    comments: '',
    type: 'Holiday'
};

const CheckmarkIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="#0d6efd" strokeWidth="2"/>
        <path d="M7.5 12.5L10.5 15.5L16.5 9.5" stroke="#0d6efd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const EnquiryModal = ({ show, handleClose, packageDetails }) => {
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };
    
    const resetForm = () => {
        setValues(initialFieldValues);
        setErrors({});
        setIsSubmitted(false);
    }

    useEffect(() => {
        if (show) {
            resetForm();
        }
    }, [show]);

    const validate = () => {
        let tempErrors = {};
        if (!values.name) tempErrors.name = "Name is required.";
        if (!values.phoneNumber) tempErrors.phoneNumber = "Phone number is required.";
        if (!values.email) {
            tempErrors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(values.email)) {
            tempErrors.email = "Email is not valid.";
        }
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) return;

        const packageId = packageDetails?.package?.packageId;
        const companyId = DefaultData.DEFAULTCOMPANYID;

        setIsSubmitting(true);
        
        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('email', values.email);
        formData.append('phoneNumber', values.phoneNumber);
        formData.append('packageId', packageId);
        formData.append('companyId', companyId);
        formData.append('type', values.type); // Ensure type is included
        if (values.comments) {
            formData.append('comments', values.comments);
        }
        try {
            const response = await apiService.post(config.CREATEPACKAGEENQUIRY, formData);
            if (response) {
                setIsSubmitted(true);
            }
        } catch (error) {
            console.error('Enquiry submission failed:', error);
            setErrors({ api: "Failed to submit enquiry. Please check your connection and try again." });
        } finally {
            setIsSubmitting(false);
        }
    };

    const ThankYouView = () => {
        const destinationName = packageDetails?.package?.destination?.destinationName || "your destination";
        const travelDate = packageDetails?.package?.startedDate 
            ? new Date(packageDetails.package.startedDate).toLocaleString('en-US', { month: 'short', year: 'numeric' })
            : "your travel date";

        return (
            <div className="text-center p-4">
                <h2 className="fw-bold mb-3">Thank You</h2>
                <p className="lead mb-2">
                    Our Holiday Expert will shortly reach out to you with our best quotations on
                </p>
                <p className="lead fw-bold mb-4">{values.phoneNumber}</p>
                <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', display: 'inline-block', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <div className="d-flex align-items-center">
                        <div className="flex-grow-1 text-start">
                            <h6 className="fw-bold mb-0">Request Received</h6>
                            <span className="text-muted">{`${destinationName} - ${travelDate}`}</span>
                        </div>
                        <div className="ms-4">
                            <CheckmarkIcon />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={handleClose} centered size={isSubmitted ? 'md' : 'lg'}>
            <Modal.Header closeButton>
                {!isSubmitted && <Modal.Title as="h5" style={{ fontWeight: 'bold' }}>Send an Enquiry</Modal.Title>}
            </Modal.Header>
            <Modal.Body className="p-4">
                {isSubmitted ? (
                    <ThankYouView />
                ) : (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">NAME*</Form.Label>
                                    <Form.Control type="text" name="name" placeholder="Name" value={values.name} onChange={handleChange} isInvalid={!!errors.name} />
                                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">PHONE*</Form.Label>
                                    <Form.Control type="tel" name="phoneNumber" placeholder="Phone Number" value={values.phoneNumber} onChange={handleChange} isInvalid={!!errors.phoneNumber} />
                                    <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">EMAIL ID*</Form.Label>
                            <Form.Control type="email" name="email" placeholder="Email" value={values.email} onChange={handleChange} isInvalid={!!errors.email} />
                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold">COMMENTS</Form.Label>
                            <Form.Control as="textarea" rows={3} name="comments" placeholder="Any specific requests or questions?" value={values.comments} onChange={handleChange}/>
                        </Form.Group>
                        {errors.api && <p className="text-danger text-center">{errors.api}</p>}
                        {/* --- UPDATED --- Button container for centering and button style */}
                        <div className="text-center mt-4">
                            <Button 
                                type="submit" 
                                disabled={isSubmitting}
                                style={{ 
                                    backgroundColor: '#4a5568', 
                                    borderColor: '#4a5568',
                                    padding: '10px 40px',
                                    fontSize: '1.1rem'
                                }}
                            >
                                {isSubmitting ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Submit'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default EnquiryModal;